/**
 * PRODUCT COMPATIBILITY - Product compatibility checks, cart filtering, and popups
 * 
 * This file contains:
 * - Product compatibility checking with delivery days
 * - Cart item compatibility checking
 * - Cart filtering and incompatible item removal
 * - Popup notifications for incompatible products
 */

import { DeliveryCartAttributes } from '@theme/delivery-cart-attributes';
import { getCurrentDeliveryDay } from '@theme/delivery-display';
import { fetchConfig } from '@theme/utilities';
import {
  getWarehouseForDeliveryDay,
  hasInventoryForDeliveryDay
} from '@theme/warehouse-inventory';


// Constants
const CONSOLE_PREFIX = '[PRODUCT-COMPATIBILITY]';

// Helper Functions
/**
 * @param {string} level - Log level (log, warn, error)
 * @param {string} message - Message to log
 * @param {...any} args - Additional arguments
 */
function logMessage(level, message, ...args) {
  // /** @type {any} */ (console)[level](`🔍 ${CONSOLE_PREFIX} ${message}`, ...args);
}

// Constants
const POPUP_CLASSES = {
  CART_ITEMS_REMOVED: 'cart-items-removed-popup',
  PRODUCT_INCOMPATIBILITY: 'product-incompatibility-popup'
};

const POPUP_TYPES = {
  CART: 'cart',
  PRODUCT: 'product',
  POSTAL_CODE_CHANGE: 'postal-code-change'
};

const POPUP_TITLES = {
  [POPUP_TYPES.CART]: 'Er zijn maaltijden uit je winkelmandje gehaald',
  [POPUP_TYPES.PRODUCT]: 'Product niet beschikbaar',
  [POPUP_TYPES.POSTAL_CODE_CHANGE]: 'Winkelwagen geleegd voor nieuwe postcode'
};

const ANIMATION_TIMEOUTS = {
  POPUP_SHOW: 10,
  POPUP_HIDE: 300
};

const SESSION_STORAGE_KEYS = {
  STEP_INFO: 'step-info',
  STEP_COUNT: 'step-count'
};

// Helper Functions
/**
 * Configure popup element from existing DOM structure
 * @param {any} popupType - Type of popup
 * @param {any} title - Popup title
 * @param {any} message - Popup message
 * @returns {HTMLElement|null} Configured popup element or null if not found
 */
function configurePopupElement(popupType, title, message) {
  const popupId = popupType === POPUP_TYPES.CART ? 'cart-items-removed-popup' : 'product-incompatibility-popup';
  const popup = document.getElementById(popupId);
  
  if (!popup) {
    logMessage('error', `Popup element with id '${popupId}' not found. Make sure the product-compatibility-popup snippet is included.`);
    return null;
  }

  logMessage('log', `Found popup element: ${popupId}`);

  // Set title
  const titleElement = popup.querySelector('.popup-title');
  if (titleElement) {
    titleElement.textContent = title;
  }

  // Set message
  const messageElement = popup.querySelector('.popup-message');
  if (messageElement) {
    messageElement.textContent = message;
  }

  // Configure view cart button for cart items removed popup
  if (popupType === POPUP_TYPES.CART) {
    const viewCartButton = popup.querySelector('.popup-button--view-cart');
    if (viewCartButton) {

      /** @type {any} */ (viewCartButton).onclick = () => {
        const backtoweekmenustep =  document.querySelector('.step-wrapper[data-step-type="maaltijden"]');
        if (backtoweekmenustep) {
          /** @type {any} */ (backtoweekmenustep).click();
          removePopup(popup);

        }
      };
    }
  }

  // Configure close button
  const closeButton = popup.querySelector('.popup-button--close');
  if (closeButton) {
    /** @type {any} */ (closeButton).onclick = () => removePopup(popup);
  }

  return popup;
}

/**
 * Hide popup with animation
 * @param {HTMLElement} popup - Popup element to hide
 */
function removePopup(popup) {
  popup.classList.remove('show');
  setTimeout(() => {
    popup.classList.remove('popup-visible');
    popup.classList.add('popup-hidden');
  }, ANIMATION_TIMEOUTS.POPUP_HIDE);
}

/**
 * Show popup with animation
 * @param {HTMLElement} popup - Popup element to show
 */
function showPopup(popup) {
  popup.classList.remove('popup-hidden');
  popup.classList.add('popup-visible');
  setTimeout(() => {
    popup.classList.add('show');
  }, ANIMATION_TIMEOUTS.POPUP_SHOW);
}

/**
 * Generate popup message based on items and type
 * @param {Array<any>} items - Items to create message for
 * @param {any} deliveryDay - Delivery day text
 * @param {any} popupType - Type of popup
 * @returns {string} Generated message
 */
function generatePopupMessage(items, deliveryDay, popupType) {
  if (popupType === POPUP_TYPES.POSTAL_CODE_CHANGE) {
    if (items.length === 1) {
      const itemName = items[0].product_title || items[0].title || 'Dit product';
      return `${itemName} is verwijderd uit je winkelwagen vanwege de nieuwe postcode.`;
    } else {
      const itemNames = items.map(item => item.product_title || item.title).join(', ');
      return `De volgende producten zijn verwijderd uit je winkelwagen vanwege de nieuwe postcode: ${itemNames}`;
    }
  }
  
  if (items.length === 1) {
    const itemName = items[0].product_title || items[0].title || 'Dit product';
    if (popupType === POPUP_TYPES.CART) {
      return `${itemName} is niet beschikbaar voor bezorging op ${deliveryDay} en is verwijderd uit je winkelwagen.`;
    } else {
      return `${itemName} is niet beschikbaar voor bezorging op ${deliveryDay}. Kies een andere bezorgdag of selecteer een ander product.`;
    }
  } else {
    const itemNames = items.map(item => item.product_title || item.title).join(', ');
    if (popupType === POPUP_TYPES.CART) {
      return `De volgende producten zijn niet beschikbaar voor bezorging op ${deliveryDay} en zijn verwijderd uit je winkelwagen: ${itemNames}`;
    } else {
      return `De volgende producten zijn niet beschikbaar voor bezorging op ${deliveryDay}: ${itemNames}`;
    }
  }
}
/**
 * Create cart update payload for removing items
 * @param {any} currentCart - Current cart data
 * @param {Set<string>} itemsToRemove - Set of item keys to remove
 * @returns {Record<string, number>} Update payload
 */
function createCartUpdatePayload(currentCart, itemsToRemove) {
  const updates = /** @type {Record<string, number>} */ ({});

  /** @type {any} */ (currentCart).items.forEach((/** @type {any} */ item) => {
    if (itemsToRemove.has(item.key)) {
      updates[item.key] = 0;
      logMessage('log', `Setting quantity to 0 for item: ${item.title} (key: ${item.key})`);
    }
  });

  return updates;
}

/**
 * Find items to remove from cart
 * @param {Array<any>} incompatibleItems - Incompatible items
 * @param {any} currentCart - Current cart data
 * @returns {Set<string>} Set of item keys to remove
 */
function findItemsToRemove(incompatibleItems, currentCart) {
  const itemsToRemove = new Set();

  incompatibleItems.forEach((/** @type {any} */ item) => {
    const matchingCartItem = /** @type {any} */ (currentCart).items.find((/** @type {any} */ cartItem) =>
      cartItem.product_id === item.product_id &&
      cartItem.variant_id === item.variant_id
    );

    if (matchingCartItem) {
      itemsToRemove.add(matchingCartItem.key);
      logMessage('log', `Marking item for removal: ${matchingCartItem.title} (key: ${matchingCartItem.key})`);
    } else {
      logMessage('warn', `Could not find matching cart item for: ${item.product_title}`);
    }
  });

  return itemsToRemove;
}

// Main Functions
/**
 * Check if product is compatible with current delivery day
 * @param {string} productId - The product ID to check
 * @param {any} [cart] - Optional cart data to avoid redundant API calls
 * @returns {Promise<boolean>} - True if product is compatible
 */
export async function isProductCompatibleWithDeliveryDay(productId, cart = undefined) {
  // Get current delivery day
  const deliveryDay = await getCurrentDeliveryDay(cart);

  if (!deliveryDay) {
    return true; // No day selected, assume compatible
  }

  // Find the product card to get inventory data
  const productCard = document.querySelector(`product-card[data-product-id="${productId}"]`);

  if (!productCard) {
    return true; // If we can't find the product card, assume it's compatible
  }


  let variantInventoryLevels = '';
  const variantSelectDropdown = productCard.querySelector('variant-select-dropdown');
  const variantOptionSelector = productCard.querySelector('variant-option-selector');
  let selectedVariant = null;
  
  if (variantSelectDropdown) {
    selectedVariant = variantSelectDropdown?.querySelector('.dropdown-menu-item[data-selected="true"]');
  }

  if (variantOptionSelector) {
    selectedVariant = variantOptionSelector?.querySelector('.dropdown-menu-item[data-selected="true"]');
  }

  if (selectedVariant) {
    variantInventoryLevels = selectedVariant.getAttribute('data-variant-inventory-levels') || '';
  }

  // Check if the product has inventory for the delivery day
  const hasInventory = variantInventoryLevels.includes("WH3") ? true : hasInventoryForDeliveryDay(variantInventoryLevels, deliveryDay || '');

  return hasInventory;
}

/**
 * Check if a cart item is compatible with the current delivery day
 * @param {any} cartItem - The cart item object from Shopify
 * @param {string} [deliveryDay] - Optional delivery day (if not provided, will fetch current)
 * @returns {Promise<boolean>} - True if item is compatible with delivery day
 */
export async function isCartItemCompatibleWithDeliveryDay(cartItem, deliveryDay = undefined) {
  // Use provided delivery day or fetch current one
  if (!deliveryDay) {
    deliveryDay = await getCurrentDeliveryDay() || undefined;
  }
  
  if (!deliveryDay) {
    return true; // No delivery day selected, assume compatible
  }

  const variantInventoryLevels = cartItem.properties?._variant_inventory_levels;

  // Get target warehouse for the delivery day
  const targetWarehouse = getWarehouseForDeliveryDay(deliveryDay);
  if (!targetWarehouse) {
    return true; // No specific day selected, assume compatible
  }

  // Check if the product has inventory for the target warehouse using utility function
  console.log("Cart time product title", cartItem.product_title);
  const hasInventory = variantInventoryLevels.includes("WH3") ? true : hasInventoryForDeliveryDay(variantInventoryLevels, deliveryDay);

  logMessage('log', `Cart item ${cartItem.product_id} compatibility check for ${deliveryDay}:`, hasInventory);
  return hasInventory;
}


/**
 * Generic function to filter and remove incompatible cart items
 * @param {any} currentCart - Current cart data
 * @param {string} filterType - The type of filter ('delivery-day' or 'postal-code')
 * @param {string} filterValue - The value to filter against (delivery day or postal code)
 * @returns {Promise<{success: boolean, removedItems: Array<any>}>} - Object with success status and removed items
 */
export async function filterIncompatibleCartItems(currentCart, filterType, filterValue) {
  try {
    if (!currentCart || !currentCart.items || currentCart.items.length === 0) {
      logMessage('log', 'No cart items to check for compatibility');
      return { success: true, removedItems: [] };
    }

    const filterTypeText = filterType === 'delivery-day' ? 'delivery day' : 'postal code';
    logMessage('log', `Checking cart compatibility for ${filterTypeText}: ${filterValue}`);

    // Find items that are NOT compatible with the filter value
    const incompatibleItems = [];
    
    for (const cartItem of currentCart.items) {
      let isCompatible;
      
      if (filterType === 'delivery-day') {
        isCompatible = await isCartItemCompatibleWithDeliveryDay(cartItem, filterValue);
      } else {
        logMessage('warn', `Unknown filter type: ${filterType}`);
        continue;
      }
      
      if (!isCompatible) {
        incompatibleItems.push(cartItem);
        logMessage('log', `Item ${cartItem.title} is incompatible with ${filterTypeText} ${filterValue}`);
      } else {
        logMessage('log', `Item ${cartItem.title} is compatible with ${filterTypeText} ${filterValue}`);
      }
    }

    if (incompatibleItems.length === 0) {
      logMessage('log', `All cart items are compatible with ${filterTypeText} ${filterValue}`);
      return { success: true, removedItems: [] };
    }

    logMessage('log', `Removing ${incompatibleItems.length} incompatible items for ${filterTypeText} change`);
    
    // Use existing function to remove the incompatible items
    const success = await removeIncompatibleCartItems(incompatibleItems, currentCart);
    return { success, removedItems: incompatibleItems };

  } catch (error) {
    logMessage('error', `Error filtering incompatible cart items for ${filterType}:`, error);
    return { success: false, removedItems: [] };
  }
}

/**
 * Remove all cart items from cart
 * @param {Array<any>} incompatibleItems - Array of cart items to remove
 * @param {any} currentCart - Current cart data
 * @param {boolean} showPopup - Whether to show popup after removal (default: true)
 * @returns {Promise<boolean>} - True if items were successfully removed
 */
export async function removeIncompatibleCartItems(incompatibleItems, currentCart, showPopup = true) {
  if (incompatibleItems.length === 0) {
    return true;
  }

  try {
    // Get current cart to ensure we have the latest data (if not provided)
    if (!currentCart) {
      if (!currentCart || !/** @type {any} */ (currentCart).items) {
        return false;
      }
    }

    // Find items to remove
    const itemsToRemove = findItemsToRemove(incompatibleItems, currentCart);

    if (itemsToRemove.size === 0) {
      logMessage('log', 'No items to remove found in current cart');
      return true;
    }

    // Create the update payload
    const updates = createCartUpdatePayload(currentCart, itemsToRemove);

    // Make a single API call to remove all incompatible items
    const body = JSON.stringify({
      updates: updates,
      sections_url: window.location.pathname,
    });

    logMessage('log', 'Sending bulk cart update request to:', Theme.routes.cart_update_url);
    logMessage('log', 'Request body:', body);

    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));

    if (!response.ok) {
      logMessage('error', 'Failed to remove cart items');
      logMessage('error', 'Response status:', response.status);
      logMessage('error', 'Response status text:', response.statusText);
      try {
        const errorText = await response.text();
        logMessage('error', 'Response body:', errorText);
      } catch (e) {
        logMessage('error', 'Could not read response body');
      }
      return false;
    }

    const responseText = await response.text();
    const parsedResponse = JSON.parse(responseText);

    if (parsedResponse.errors) {
      logMessage('error', 'Cart update errors:', parsedResponse.errors);
      return false;
    }

    logMessage('log', 'Successfully removed all cart items');
    logMessage('log', 'Response sections received:', Object.keys(parsedResponse.sections || {}));

    // Show popup with removed items if any
    if (showPopup && incompatibleItems.length > 0) {
      try {
        const deliveryDay = await getCurrentDeliveryDay();
        showItemsRemovedPopup(incompatibleItems, deliveryDay || 'deze dag', POPUP_TYPES.CART);
      } catch (error) {
        logMessage('warn', 'Failed to show popup for removed items:', error);
      }
    }

    return true;
  } catch (error) {
    logMessage('error', 'Error removing cart items:', error);
    return false;
  }
}
/**
 * Show popup notification for items removed or incompatible products
 * @param {Array<any>} removedItems - Array of items that were removed or are incompatible
 * @param {any} deliveryDay - The delivery day
 * @param {any} popupType - Type of popup ('cart' or 'product')
 */
export function showItemsRemovedPopup(removedItems, deliveryDay, popupType) {
  if (!removedItems || removedItems.length === 0) return;

  // Check if a popup already exists and hide it
  const existingPopup = document.querySelector(`.${POPUP_CLASSES.CART_ITEMS_REMOVED}, .${POPUP_CLASSES.PRODUCT_INCOMPATIBILITY}`);
  if (existingPopup) {
    existingPopup.classList.remove('show');
    existingPopup.classList.remove('popup-visible');
    existingPopup.classList.add('popup-hidden');
  }

  const dayText = 'deze dag';
  const title = POPUP_TITLES[popupType] || POPUP_TITLES[POPUP_TYPES.CART];
  const message = generatePopupMessage(removedItems, dayText, popupType);

  const popup = configurePopupElement(popupType, title, message);
  if (popup) {
    showPopup(popup);
  }
}

/**
 * Show popup notification for incompatible product
 * @param {string} productId - The product ID that is incompatible
 */
export async function showIncompatibilityPopup(productId) {
  // Get current delivery day using utility function
  const deliveryDay = await getCurrentDeliveryDay();

  // Get product title
  const productCard = document.querySelector(`product-card[data-product-id="${productId}"]`);
  const productTitle = productCard?.querySelector('.product-card__title')?.textContent || 'Dit product';

  // Create a mock item object for the centralized popup function
  const mockItem = { product_title: productTitle };

  // Use centralized popup function
  showItemsRemovedPopup([mockItem], /** @type {any} */ (deliveryDay || 'deze dag'), POPUP_TYPES.PRODUCT);
}

/**
 * Show popup notification for removed cart items
 * @param {Array<any>} removedItems - Array of cart items that were removed
 * @param {any} deliveryDay - The delivery day that caused the removal
 */
export function showCartItemsRemovedPopup(removedItems, deliveryDay) {
  showItemsRemovedPopup(removedItems, deliveryDay, POPUP_TYPES.CART);
}

/**
 * Show popup notification for postal code change cart clearing
 * @param {Array<any>} removedItems - Array of cart items that were removed
 */
export function showPostalCodeChangePopup(removedItems) {
  showItemsRemovedPopup(removedItems, null, POPUP_TYPES.POSTAL_CODE_CHANGE);
}


// Export for global access
if (typeof window !== "undefined") {
  /** @type {any} */ (window).ProductCompatibility = {
    isProductCompatibleWithDeliveryDay,
    isCartItemCompatibleWithDeliveryDay,
    filterIncompatibleCartItems,
    removeIncompatibleCartItems,
    showItemsRemovedPopup,
    showIncompatibilityPopup,
    showCartItemsRemovedPopup,
    showPostalCodeChangePopup,
  };

  // Backward compatibility functions
  /** @type {any} */ (window).isProductCompatibleWithDeliveryDay = isProductCompatibleWithDeliveryDay;
  /** @type {any} */ (window).showIncompatibilityPopup = showIncompatibilityPopup;
  /** @type {any} */ (window).removeIncompatibleCartItems = removeIncompatibleCartItems;

}
